<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import { useAuthStore } from "~/plugins/store/auth";
import { translation, useTranslationStore } from "~/plugins/store/translations";
import type { TranslationList } from "~/types/Utils";
import { useNuxtApp } from "#app";
import { useUserStore } from "~/plugins/store/user";
import { useReCaptcha } from "vue-recaptcha-v3";
import LoadingDialog from "~/components/utils/LoadingDialog.vue";
import AccountLayout from "~/layouts/AccountLayout.vue";
import {useVehicleDiaryStore} from "~/plugins/store/vehicle-diary";
import type {VehicleDiary} from "~/types/VehicleDiary";
import {recaptcha} from "~/composables/recaptcha";
import {type ApiVehicleDetails, createDefaultApiVehicleDetails} from "~/types/Vehicle";
import {useVehicleStore} from "~/plugins/store/vehicle";

export default defineComponent({
  components: { AccountLayout, LoadingDialog },
  setup() {
    definePageMeta({
      middleware: ['auth'],
    });

    useHead({
      title: 'Xiga | Jurnal de mașină',
      meta: [
        {
          name: 'description',
          content: 'META'
        }
      ],
    });

    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});
    const authStore = useAuthStore();
    const user = ref(authStore.getUser());
    let accessToken = authStore.getAccessToken();
    const { $toast } = useNuxtApp();
    const userStore = useUserStore();
    const recaptchaInstance = useReCaptcha();
    const showLoadingDialog = ref(false);
    const messageLoadingDialog = ref('');
    const route = useRoute();
    const vehicleId = parseInt(route.params.id as string, 10);
    const vehicleDiaryStore = useVehicleDiaryStore();
    const diaries = ref<VehicleDiary[]>([]);
    const vehicleStore = useVehicleStore();
    const vehicle = ref(createDefaultApiVehicleDetails() as ApiVehicleDetails | null);

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      if (accessToken) {
        showLoadingDialog.value = true;
        messageLoadingDialog.value = 'Se încarcă datele...';
        vehicle.value = await vehicleStore.getVehicle(vehicleId, accessToken);
        const token = <string>await recaptcha(recaptchaInstance);
        diaries.value = await vehicleDiaryStore.fetchVehicleDiaries(vehicleId, accessToken, token);
        showLoadingDialog.value = false;
      }
    });

    const getManufacturerLogo = (manufacturer: string) => {
      const imagePath = '/images/cars/' + manufacturer + '.png';
      return imageExists(imagePath) ? imagePath : '/images/cars/no-make.png';
    };

    const imageExists = (url: string) => {
      try {
        const http = new XMLHttpRequest();
        http.open('HEAD', url, false);
        http.send();
        return http.status !== 404;
      } catch (error) {
        return false;
      }
    };

    const getExpirationClass = (expirationDate: string | null | undefined) => {
      if (!expirationDate) return 'text-red';
      const now = new Date();
      const expiration = new Date(expirationDate);
      const diffTime = expiration.getTime() - now.getTime();
      const diffDays = diffTime / (1000 * 3600 * 24);

      if (diffDays < 0) {
        return 'text-red';
      } else if (diffDays < 60) {
        return 'text-warning';
      } else {
        return 'text-success';
      }
    };

    return {
      user,
      accessToken,
      translationList,
      translation,
      showLoadingDialog,
      messageLoadingDialog,
      getManufacturerLogo,
      getExpirationClass,
      diaries,
      vehicle
    };
  }
});
</script>

<template>
  <account-layout class="profile">
    <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
    <v-container class="content-container" v-if="vehicle">
      <v-container fluid class="component-container-1400mw">
        <v-container class="background-ghost-white" style="border-radius: 15px;">
          <v-row class="mt-3">
            <v-col cols="12" sm="auto" md="12" class="d-flex flex-column justify-start centered-on-mobile">
              <h3 class="text-primary">
                <span class="cursor-pointer" @click="navigateTo('/account/vehicles/' + vehicle.id + '/')">
                  {{ vehicle.rca_vehicle_make_name }} {{ vehicle.rca_vehicle_model_name }}
                </span> <v-icon>mdi-chevron-right</v-icon>
                {{ translation(translationList.vehicle_diaries, 'Jurnale de mașină') }}
              </h3>
              <p class="font-weight-regular">
                {{
                  translation(translationList.my_profile_helper, 'Aici puteți vizualiza și modifica jurnalele de mașină pentru ')
                }} <span class="font-weight-bold">{{ vehicle.registration_plate }} {{ vehicle.vin }}</span>.
              </p>
            </v-col>
          </v-row>
          <v-container class="mt-5">
            <v-row>
              <v-col cols="12" class="d-flex justify-end align-center">
                <v-btn
                    color="primary"
                    @click="navigateTo('/account/vehicles/' + vehicle.id + '/diaries/create')"
                    size="large"
                >
                  {{ translation(translationList.my_profile, 'Adaugă jurnal') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
          <v-row v-if="diaries && diaries.length">
            <v-col cols="12" v-for="(diary, index) in diaries" :key="index">
              <v-card class="cursor-pointer" @click="navigateTo('/account/vehicles/' + vehicle.id + '/diaries/' + diary.id)">
                <v-row class="my-3 mx-3">
                  <v-col cols="10">
                    <div class="font-weight-bold" v-if="diary.event_type">
                      {{ diary.event_type.name }}
                    </div>
                    <small>
                      {{ diary.event_date }}
                    </small>
                  </v-col>
                  <v-col cols="2" class="d-flex justify-end align-center">
                    {{ translation(translationList.my_profile, 'Editează') }} <v-icon>mdi-chevron-right</v-icon>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-container>
    </v-container>
  </account-layout>
</template>

<style scoped>

</style>
